import React, { useState, useRef, useEffect } from 'react';
import { Play, Maximize2, X, Volume2, VolumeX } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export default function VideoPlayer({ src, className = '', autoPlay = false, enableMobileExpand = false }) {
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  // If not autoplaying, show the play button overlay by default
  const [showPlayButton, setShowPlayButton] = useState(!autoPlay);
  const [isExpanded, setIsExpanded] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isSeeking, setIsSeeking] = useState(false);
  const [muted, setMuted] = useState(true);
  const videoRef = useRef(null);
  const modalVideoRef = useRef(null);

  const handleVideoEnd = () => {
    setIsPlaying(false);
    setShowPlayButton(true);
  };

  const handlePlay = () => {
    if (videoRef.current) {
      videoRef.current.play();
      setIsPlaying(true);
      setShowPlayButton(false);
    }
  };

  const handleExpand = () => {
    // sync modal to current time
    if (videoRef.current && modalVideoRef.current) {
      modalVideoRef.current.currentTime = videoRef.current.currentTime;
      modalVideoRef.current.muted = muted;
    }
    setIsExpanded(true);
  };

  const handleClose = () => {
    // sync back to main when closing
    if (videoRef.current && modalVideoRef.current) {
      videoRef.current.currentTime = modalVideoRef.current.currentTime;
      videoRef.current.muted = muted;
    }
    setIsExpanded(false);
  };

  const handleLoadedMetadata = (e) => {
    const d = e.currentTarget.duration || 0;
    if (!Number.isNaN(d)) setDuration(d);
  };

  const handleTimeUpdate = (e) => {
    if (!isSeeking) {
      const t = e.currentTarget.currentTime || 0;
      setCurrentTime(t);
    }
  };

  const handleSeekStart = () => setIsSeeking(true);
  const handleSeekEnd = () => setIsSeeking(false);
  const handleSeekChange = (e) => {
    const val = parseFloat(e.target.value);
    setCurrentTime(val);
    const activeRef = isExpanded && modalVideoRef.current ? modalVideoRef : videoRef;
    if (activeRef.current) {
      activeRef.current.currentTime = val;
    }
  };

  useEffect(() => {
    // reset progress on source change and restore initial play state
    setDuration(0);
    setCurrentTime(0);
    setIsPlaying(autoPlay);
    setShowPlayButton(!autoPlay);
  }, [src, autoPlay]);

  useEffect(() => {
    const vs = [videoRef.current, modalVideoRef.current];
    vs.forEach((v) => {
      if (v) v.muted = muted;
    });
  }, [muted]);

  const toggleMute = () => {
    setMuted((prev) => {
      const next = !prev;
      const vs = [videoRef.current, modalVideoRef.current];
      vs.forEach((v) => {
        if (v) {
          v.muted = next;
          try { v.play(); } catch (_) {}
        }
      });
      return next;
    });
  };

  return (
    <>
      {/* Normal Video Display - Hidden when expanded on mobile */}
      <div className={`relative group ${isExpanded ? 'md:block hidden' : ''}`}>
        <video
          ref={videoRef}
          src={src}
          autoPlay={autoPlay}
          muted={muted}
          playsInline
          className={className}
          onEnded={handleVideoEnd}
          onLoadedMetadata={handleLoadedMetadata}
          onTimeUpdate={handleTimeUpdate}
        />

        {showPlayButton && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            whileHover={{ scale: 1.1 }}
            onClick={handlePlay}
            className="absolute inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm rounded-lg transition-all"
          >
            <div className="bg-[#CBB26A] rounded-full p-6 shadow-2xl">
              <Play className="w-12 h-12 text-black fill-black" />
            </div>
          </motion.button>
        )}

        {/* Mobile Expand Button */}
        {enableMobileExpand && !isExpanded && (
          <button
            onClick={handleExpand}
            className="md:hidden absolute top-4 right-4 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full transition-all"
            aria-label="Expand video"
          >
            <Maximize2 className="w-5 h-5" />
          </button>
        )}
        {/* Mute/Unmute Toggle */}
        <button
          onClick={toggleMute}
          className="absolute bottom-4 left-4 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full transition-all"
          aria-label={muted ? 'Unmute video' : 'Mute video'}
        >
          {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
        </button>
        {/* Seek slider */}
        {duration > 0 && (
          <div className="mt-2 px-2">
            <input
              type="range"
              min={0}
              max={duration || 0}
              step="0.05"
              value={Number.isFinite(currentTime) ? currentTime : 0}
              onMouseDown={handleSeekStart}
              onTouchStart={handleSeekStart}
              onMouseUp={handleSeekEnd}
              onTouchEnd={handleSeekEnd}
              onChange={handleSeekChange}
              aria-label="Seek video"
              className="w-full h-2 bg-gray-800/80 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-[10px] text-neutral-400 mt-1">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>
        )}
      </div>

      {/* Expanded Modal (Mobile Only) */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black flex items-center justify-center p-4 md:hidden"
            onClick={handleClose}
          >
            {/* Close Button */}
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 bg-black/60 hover:bg-black/80 text-white p-3 rounded-full transition-all z-10"
              aria-label="Close expanded view"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Expanded Video */}
            <motion.video
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              ref={modalVideoRef}
              src={src}
              autoPlay
              muted={muted}
              playsInline
              loop
              className="w-full h-auto max-h-full rounded-lg"
              onLoadedMetadata={handleLoadedMetadata}
              onTimeUpdate={handleTimeUpdate}
              onClick={(e) => e.stopPropagation()}
            />

            {/* Mute/Unmute Toggle in modal */}
            <button
              onClick={(e) => { e.stopPropagation(); toggleMute(); }}
              className="absolute bottom-4 left-4 bg-black/60 hover:bg-black/80 text-white p-3 rounded-full transition-all z-10"
              aria-label={muted ? 'Unmute video' : 'Mute video'}
            >
              {muted ? <VolumeX className="w-6 h-6" /> : <Volume2 className="w-6 h-6" />}
            </button>

            {/* Seek slider in modal */}
            {duration > 0 && (
              <div className="w-full max-w-3xl mt-3">
                <input
                  type="range"
                  min={0}
                  max={duration || 0}
                  step="0.05"
                  value={Number.isFinite(currentTime) ? currentTime : 0}
                  onMouseDown={handleSeekStart}
                  onTouchStart={handleSeekStart}
                  onMouseUp={handleSeekEnd}
                  onTouchEnd={handleSeekEnd}
                  onChange={handleSeekChange}
                  aria-label="Seek video"
                  className="w-full h-2 bg-gray-800/80 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-[10px] text-neutral-400 mt-1">
                  <span>{formatTime(currentTime)}</span>
                  <span>{formatTime(duration)}</span>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function formatTime(seconds) {
  if (!Number.isFinite(seconds)) return '0:00';
  const s = Math.floor(seconds % 60)
    .toString()
    .padStart(2, '0');
  const m = Math.floor(seconds / 60)
    .toString()
    .padStart(1, '0');
  return `${m}:${s}`;
}
